import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import {
  acceptInvitation,
  declineInvitation,
  getInvitationDetails,
} from "@/queries/team";
import { Clock, User, UserCheck, Users } from "lucide-react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

function InvitationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");
  const { toast } = useToast();

  const [isProcessing, setIsProcessing] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [actionTaken, setActionTaken] = useState<
    "accepted" | "declined" | null
  >(null);

  const {
    data: invitation,
    isLoading,
    error,
  } = getInvitationDetails({
    token: token || "",
    enabled: !!token,
  });

  const { mutateAsync: acceptMutation } = acceptInvitation();
  const { mutateAsync: declineMutation } = declineInvitation();

  const handleAccept = async () => {
    if (!token) return;

    setIsProcessing(true);
    try {
      const response = await acceptMutation({ token });
      if (response.success) {
        setActionTaken("accepted");
        setIsCompleted(true);
        toast({
          title: "Invitation Accepted",
          description: "You have successfully joined the team!",
        });
        // Redirect to teams page after a short delay
        setTimeout(() => navigate("/setup/teams"), 2000);
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Failed to accept invitation",
        description: error.message || "Something went wrong.",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!token) return;

    setIsProcessing(true);
    try {
      const response = await declineMutation({ token });
      if (response.success) {
        setActionTaken("declined");
        setIsCompleted(true);
        toast({
          title: "Invitation Declined",
          description: "You have declined the team invitation.",
        });
        // Redirect to home page after a short delay
        setTimeout(() => navigate("/"), 2000);
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Failed to decline invitation",
        description: error.message || "Something went wrong.",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading invitation details...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error || !invitation || !token) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl text-red-600">
              Invalid Invitation
            </CardTitle>
            <CardDescription>
              This invitation link is invalid or has expired.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button
              className="w-full"
              variant="outline"
              onClick={() => navigate("/")}
            >
              Go Home
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Success state
  if (isCompleted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl text-green-600">
              {actionTaken === "accepted"
                ? "Invitation Accepted! 🎉"
                : "Invitation Declined"}
            </CardTitle>
            <CardDescription>
              {actionTaken === "accepted"
                ? `Welcome to ${invitation.team_name}! You will be redirected shortly.`
                : "You have declined the invitation. You will be redirected shortly."}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Main invitation display
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl">Team Invitation</CardTitle>
          <CardDescription>You've been invited to join a team</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            {/* Team Information */}
            <div className="flex items-center space-x-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <Users className="h-8 w-8 text-blue-600" />
              <div>
                <h3 className="font-semibold text-lg">
                  {invitation.team_name}
                </h3>
                {invitation.team_description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {invitation.team_description}
                  </p>
                )}
              </div>
            </div>

            {/* Role Information */}
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-center space-x-2">
                <UserCheck className="h-5 w-5 text-gray-500" />
                <span className="text-sm font-medium">Role:</span>
              </div>
              <Badge variant="secondary" className="uppercase">
                {invitation.role}
              </Badge>
            </div>

            {/* Inviter Information */}
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-center space-x-2">
                <User className="h-5 w-5 text-gray-500" />
                <span className="text-sm font-medium">Invited by:</span>
              </div>
              <span className="text-sm">{invitation.inviter_name}</span>
            </div>

            {/* Expiry Information */}
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-center space-x-2">
                <Clock className="h-5 w-5 text-gray-500" />
                <span className="text-sm font-medium">Expires:</span>
              </div>
              <span className="text-sm text-orange-600">
                {new Date(invitation.expires_at).toLocaleDateString()} at{" "}
                {new Date(invitation.expires_at).toLocaleTimeString()}
              </span>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex space-x-3">
          <Button
            className="flex-1"
            onClick={handleAccept}
            disabled={isProcessing}
          >
            {isProcessing ? "Processing..." : "Accept Invitation"}
          </Button>
          <Button
            className="flex-1"
            variant="outline"
            onClick={handleDecline}
            disabled={isProcessing}
          >
            {isProcessing ? "Processing..." : "Decline"}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

export default InvitationPage;
